// Mirror of mkback Brand.java enum in TypeScript for frontend single source of truth
// Backend: com.travelandleisure.mako.mkback.enums.Brand

export type BrandKey = 'CLUBWYNDHAM' | 'WORLDMARK' | 'SPORTSILLUSTRATED' | 'EVEREST'

export interface BrandDef {
  key: BrandKey
  number: string
  shortName: string
  displayNames: string[]
  clubId: string
  videoSrc: string
}

// Data copied to match backend enum (with the special getNumber quirk for EVEREST mapping 003->001)
const defs: Record<BrandKey, BrandDef> = {
  CLUBWYNDHAM: {
    key: 'CLUBWYNDHAM',
    number: '000',
    shortName: 'cw',
    displayNames: ['club wyndham', 'clubwyndham', 'wvr'],
    clubId: 'WVR',
    videoSrc: '/content/images/sir/StadiumCheering.mp4',
  },
  WORLDMARK: {
    key: 'WORLDMARK',
    number: '001',
    shortName: 'wm',
    displayNames: ['worldmark', 'wbw'],
    clubId: 'WBW',
    videoSrc: '/content/images/wm/wm-login-video.mp4',
  },
  SPORTSILLUSTRATED: {
    key: 'SPORTSILLUSTRATED',
    number: '005',
    shortName: 'sir',
    displayNames: ['sports illustrated', 'sports', 'sir'],
    clubId: 'SIR',
    videoSrc: '/content/images/sir/StadiumCheering.mp4',
  },
  EVEREST: {
    key: 'EVEREST',
    number: '003',
    shortName: 'ebac',
    displayNames: ['everest', 'ebac', 'ev', 'eddie bauer adventure club'],
    clubId: 'EBAC',
    videoSrc: '/content/images/ebac/ebac-login-video.mp4',
  },
}

// Backend Brand.getNumber has a special case mapping 003 -> 001
function normalizedNumber(number: string): string {
  if (number === '003') return '001'
  return number
}

export const Brand = {
  defs,
  keys: Object.keys(defs) as BrandKey[],
  videoSrc(brand: BrandDef): string {
    return brand.videoSrc
  },
  videoSrcFor(input: string): string {
    const def = this.fromShortOrNumber(input)
    return this.videoSrc(def)
  },
  tryVideoSrcFor(input: string): string | undefined {
    try {
      return this.videoSrcFor(input)
    } catch {
      return undefined
    }
  },
  fromString(brandEnumName: string): BrandDef {
    const key = (brandEnumName || '').toUpperCase() as BrandKey
    if (defs[key]) return defs[key]
    throw new Error(`No enum constant for brand name: ${brandEnumName}`)
  },
  fromShortName(short: string): BrandDef {
    const lower = (short || '').toLowerCase()
    const found = (Object.values(defs) as BrandDef[]).find((b) => b.shortName.toLowerCase() === lower)
    if (found) return found
    throw new Error(`No enum constant for short name: ${short}`)
  },
  fromDisplayName(display: string): BrandDef {
    const lower = (display || '').toLowerCase()
    const found = (Object.values(defs) as BrandDef[]).find((b) => b.displayNames.includes(lower))
    if (found) return found
    throw new Error(`No enum constant for display name: ${display}`)
  },
  fromNumber(num: string): BrandDef {
    const found = (Object.values(defs) as BrandDef[]).find((b) => b.number === num)
    if (found) return found
    throw new Error(`No enum constant for number: ${num}`)
  },
  from(value: string): BrandDef {
    const v = (value || '').toLowerCase()
    const found = (Object.values(defs) as BrandDef[]).find((b) => b.shortName.toLowerCase() === v || b.number === value || b.displayNames.includes(v))
    if (found) return found
    throw new Error(`No enum constant for value: ${value}`)
  },
  fromShortOrNumber(input: string): BrandDef {
    return /^\d{3}$/.test(input) ? this.fromNumber(input) : this.fromShortName(input)
  },
  toNumericId(input: string): string {
    return /^\d{3}$/.test(input) ? input : this.getNumber(this.fromShortName(input))
  },
  getNumber(brand: BrandDef): string {
    return normalizedNumber(brand.number)
  },
  getId(input: string): string {
    if (!input) return ''
    const brand = this.from(input)
    return this.getNumber(brand)
  },
  getKey(input: string): string {
    if (!input) return ''
    const brand = this.from(input)
    return brand.shortName
  },
}

export function brandDisplayName(input: BrandDef | string | undefined): string {
  const b =
    typeof input === 'string'
      ? (() => {
          try {
            return Brand.fromShortName(input)
          } catch (e) {
            /* no-op */
          }
          try {
            return Brand.fromNumber(input)
          } catch (e) {
            /* no-op */
          }
          try {
            return Brand.fromString(input)
          } catch (e) {
            /* no-op */
          }
          try {
            return Brand.fromDisplayName(input)
          } catch (e) {
            /* no-op */
          }
          return undefined
        })()
      : input
  if (!b) return 'Worldmark Resorts'
  switch (b.shortName) {
    case 'wm':
      return 'Worldmark Resorts'
    case 'cw':
      return 'Club Wyndham Resorts'
    case 'ebac':
      return 'Eddie Bauer Adventure Club'
    case 'sir':
      return 'Sports Illustrated Resorts'
    default:
      return 'Worldmark Resorts'
  }
}

export function brandSiteKey(brand: BrandDef): string {
  switch (brand.shortName) {
    case 'wm':
      return 'siteWorldmark'
    case 'cw':
      return 'siteWyndham'
    case 'ebac':
      return 'siteEverest'
    case 'sir':
      return 'siteSports'
    default:
      return 'siteWorldmark'
  }
}
